import { Badge, Tooltip } from '@plerionhq/design-system';
import { scopeDescriptions, scopeDisplay } from '../label-maps';
import type { GrantScope } from '../types';
import { useTranslation } from 'react-i18next';

type ScopeBadgeProperties = {
  scope: GrantScope;
};

// U+2011 non-breaking hyphen and U+00A0 non-breaking space. Built via
// fromCharCode so the source stays plain ASCII (no invisible characters).
const NON_BREAKING_HYPHEN = String.fromCharCode(0x2011);
const NON_BREAKING_SPACE = String.fromCharCode(0x00a0);

// The design-system Badge label is allowed to wrap (`text-wrap`), so in the
// grants table's auto-layout the browser breaks hyphenated/spaced scope labels
// (Same-org, Cross-account, "AWS service") onto two lines when the column is
// tight — and a wrapper `whitespace-nowrap` doesn't hold because the label sets
// its own `text-wrap`. Swapping the hyphens/spaces for non-breaking equivalents
// makes each label a single unbreakable token, so the column auto-sizes to fit
// it on one line. Used by /access-grants and the CIEM External access table.
export function ScopeBadge({ scope }: ScopeBadgeProperties) {
  const { t } = useTranslation('accessGrants');
  const { label, status } = scopeDisplay(t)[scope] ?? {
    label: scope,
    status: 'neutral' as const,
  };
  const unbreakableLabel = label
    .replace(/-/g, NON_BREAKING_HYPHEN)
    .replace(/ /g, NON_BREAKING_SPACE);
  return (
    <Tooltip label={scopeDescriptions(t)[scope]}>
      <Badge label={unbreakableLabel} status={status} />
    </Tooltip>
  );
}
